import { css } from '@emotion/react';
import { useState } from 'react';
import t from 'react-translate';
import { standardSpacing, largeSpacing, quarterSpacing, halfSpacing } from 'styles/global_defaults/scaffolding';
import { warning, danger, gray4 } from 'styles/global_defaults/colors';
import NvIcon from 'shared/components/nv-icon';
import { NvTooltip } from 'shared/components/nv-tooltip';
import { NvPopover } from 'shared/components/nv-popover';
import ClickableContainer from 'components/clickable-container';


type NvStackedTabProps = {
  count: number,
  isStacked?: boolean,
  hasError?: boolean,
  errorMessage?: string,
  onClick?: () => void,
  isExpanded?: boolean,
};

const styles = (hasError: boolean, isExpanded: boolean) => {
  let bgColor = isExpanded ? gray4 : warning;
  bgColor = hasError ? danger : bgColor;
  const width = 45;

  return css`
    outline: none;
    border: none;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;

    .error {
      color: ${danger};
    }

    .tab {
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${width}px;
      height: ${largeSpacing}px;
      background-color: ${bgColor};
      color: white;
      border-top-right-radius: ${standardSpacing}px;
      border-bottom-right-radius: ${standardSpacing}px;
      position: absolute;
      transform: translateZ(0);

      &.stacked {
        position: absolute;
        top: ${halfSpacing}px;
        right: -${width}px;
        z-index: 1;

        &:before {
          position: absolute;
          content: '';
          width: 100%;
          height: 100%;
          top: ${quarterSpacing}px;
          left: 0px;
          border-top-right-radius: ${standardSpacing}px;
          border-bottom-right-radius: ${standardSpacing}px;
          background-color: ${bgColor};
          opacity: .6;
          z-index: -1;
        }

        &:after {
          position: absolute;
          content: '';
          width: 100%;
          height: 100%;
          top: ${halfSpacing}px;
          left: 0px;
          border-top-right-radius: ${standardSpacing}px;
          border-bottom-right-radius: ${standardSpacing}px;
          background-color: ${bgColor};
          opacity: .4;
          z-index: -1;
        }
      }

      .error {
        position: absolute;
        top: -14px;
        left: 32%;
        color: ${danger};
        .icon {
          margin: 0;
        }
      }
    }
  `;
};

const warningPopover = (errorMessage: string) => (
  <div>
    <div className='text-center text-danger bold'>{ t.FORM.WARNING() }</div>
    <p className='mt-2 mb-0'>{ errorMessage }</p>
  </div>
);

const NvStackedTab = ({
  count,
  isStacked,
  hasError,
  errorMessage,
  onClick,
  isExpanded,
}: NvStackedTabProps) => {
  const [showWarning, setShowWarning] = useState(false);

  return (
    <ClickableContainer css={styles(hasError, isExpanded)} className='text-small d-flex align-items-center' onClick={onClick}>
      {hasError ? (
        <div
          className={`tab ${isStacked && 'stacked'}`}
          onMouseEnter={() => setShowWarning(true)}
          onMouseLeave={() => setShowWarning(false)}
        >
          <NvPopover
            content={warningPopover(errorMessage)}
            placement='top'
            preventOverflow
            showOnHover={false}
            show={showWarning}
            offset={40}
          >
            <div className='error'>
              <NvIcon icon='warning' size='xss-smallest' />
            </div>
          </NvPopover>
          {count}
        </div>
      ) : (
        <NvTooltip text={isExpanded ? t.COURSE_COMMUNICATIONS.TOOLTIP.CLICK_TO_HIDE_COMMUNICATIONS() : t.COURSE_COMMUNICATIONS.TOOLTIP.CLICK_TO_VIEW_COMMUNICATIONS()} placement='top'>
          <div className={`tab ${isStacked && 'stacked'}`}>
            {count}
          </div>
        </NvTooltip>
      )}
    </ClickableContainer>
  );
};

export default NvStackedTab;
